import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Icon,
  useToast,
  Skeleton,
  SkeletonText,
} from '@chakra-ui/react';
import {
  FaCheckCircle,
  FaUtensils,
  FaStore,
  FaGamepad,
  FaPalette,
  FaGift,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import VantaggiEsclusiviModal from '../components/Subscription/VantaggiEsclusiviModal';

// Helper per ottenere l'icona in base al tipo di sconto
const getTypeIcon = (type) => {
  const typeIcons = {
    ristorante: FaUtensils,
    negozio: FaStore,
    attivita: FaGamepad,
    cultura: FaPalette,
    servizi: FaGift,
    default: FaGift,
  };
  return typeIcons[type?.toLowerCase()] || typeIcons.default;
};

// Helper per ottenere il colore del badge in base al tipo
const getTypeColor = (type) => {
  const typeColors = {
    ristorante: 'orange',
    negozio: 'blue',
    attivita: 'green',
    cultura: 'purple',
    servizi: 'blue',
    default: 'gray',
  };
  return typeColors[type?.toLowerCase()] || typeColors.default;
};

const CILplus = () => {
  const { userData } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(true);
  const [selectedSconto, setSelectedSconto] = useState(null);
  const [isVantaggiModalOpen, setIsVantaggiModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [totalDonatedFromInvoices, setTotalDonatedFromInvoices] =
    useState(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const toast = useToast();

  // Controllo accesso - solo per utenti con subscription attiva
  if (!userData || userData.subscriptionStatus !== 'active') {
    return <Navigate to="/" replace />;
  }

  // Fetch subscription tiers con tutti i dati (sconti, sponsor, eventi) da Strapi
  useEffect(() => {
    const fetchTiersWithData = async () => {
      try {
        // Fetch dei subscription tiers con populate completo di sconti, sponsor ed eventi
        const response = await fetch('/api/subscription-tiers?populate=*');
        if (response.ok) {
          const data = await response.json();
          const tiersArray = Array.isArray(data)
            ? data
            : data?.data && Array.isArray(data.data)
              ? data.data
              : [];

          // Filtra solo i tier che l'utente ha attivamente
          const userActiveTiers = tiersArray.filter((tier) => {
            return userData.subscriptions?.some(
              (sub) =>
                (sub.subscriptionStatus === 'active' ||
                  sub.status === 'active') &&
                sub.subscriptionTier?.documentId === tier.documentId
            );
          });

          setTiers(userActiveTiers);
        }
      } catch (error) {
        console.error(
          'Errore nel caricamento dei tier con dati completi:',
          error
        );
        toast({
          title: 'Errore nel caricamento dei piani di abbonamento',
          status: 'error',
          duration: 3000,
        });
      } finally {
        setLoadingTiers(false);
      }
    };

    if (userData?.id) {
      fetchTiersWithData();
    } else {
      setLoadingTiers(false);
    }
  }, [userData?.id, userData.subscriptions, toast]);

  // Fetch delle invoice per calcolare il totale donato da Stripe
  useEffect(() => {
    const fetchInvoicesTotal = async () => {
      const activeSubscription = userData.subscriptions?.find(
        (sub) => sub.subscriptionStatus === 'active' || sub.status === 'active'
      );

      if (!activeSubscription) {
        return;
      }

      setLoadingInvoices(true);
      try {
        const response = await fetch(
          `/api/subscriptions/${activeSubscription.documentId}/invoices`
        );
        if (response.ok) {
          const data = await response.json();
          setTotalDonatedFromInvoices(parseFloat(data.totalPaidFormatted));
        } else {
          console.error(
            'Errore nel recupero delle invoice:',
            response.statusText
          );
          // Fallback al calcolo manuale se l'endpoint fallisce
          setTotalDonatedFromInvoices(null);
        }
      } catch (error) {
        console.error('Errore nel recupero delle invoice:', error);
        // Fallback al calcolo manuale se l'endpoint fallisce
        setTotalDonatedFromInvoices(null);
      } finally {
        setLoadingInvoices(false);
      }
    };

    if (userData?.id && userData.subscriptions) {
      fetchInvoicesTotal();
    }
  }, [userData?.id, userData.subscriptions]);

  // Formatta la data di fine periodo
  const formatDate = (dateString) => {
    if (!dateString) return 'Non disponibile';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch (error) {
      return 'Data non valida';
    }
  };

  // Funzione per aprire il modale dei vantaggi esclusivi
  const openVantaggiModal = (sconto) => {
    setSelectedSconto(sconto);
    setIsVantaggiModalOpen(true);
  };

  // Funzione per aprire il modal di conferma cancellazione
  const openCancelModal = () => {
    setIsCancelModalOpen(true);
  };

  // Funzione per cancellare la subscription
  const cancelSubscription = async () => {
    setIsCancelling(true);

    try {
      const activeSubscription = userData.subscriptions?.find(
        (sub) => sub.subscriptionStatus === 'active' || sub.status === 'active'
      );

      if (!activeSubscription) {
        toast({
          title: 'Errore',
          description: 'Nessuna subscription attiva trovata',
          status: 'error',
          duration: 3000,
        });
        return;
      }

      const response = await fetch('/api/subscriptions/cancel', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          localSubscriptionId: activeSubscription.documentId,
        }),
      });

      if (response.ok) {
        toast({
          title: 'Subscription cancellata',
          description:
            'La tua subscription sarà disattivata alla fine del periodo corrente',
          status: 'success',
          duration: 5000,
        });

        // Chiudi il modal e ricarica la pagina
        setIsCancelModalOpen(false);
        window.location.reload();
      } else {
        const errorData = await response.json();
        toast({
          title: 'Errore',
          description:
            errorData.message ||
            'Errore durante la cancellazione della subscription',
          status: 'error',
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('Errore nel cancellare la subscription:', error);
      toast({
        title: 'Errore',
        description: 'Errore di connessione. Riprova più tardi.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsCancelling(false);
    }
  };

  // Funzione per riattivare la subscription
  const reactivateSubscription = async () => {
    try {
      const activeSubscription = userData.subscriptions?.find(
        (sub) => sub.subscriptionStatus === 'active' || sub.status === 'active'
      );

      if (!activeSubscription) {
        toast({
          title: 'Errore',
          description: 'Nessuna subscription attiva trovata',
          status: 'error',
          duration: 3000,
        });
        return;
      }

      const response = await fetch('/api/subscriptions/reactivate', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          localSubscriptionId: activeSubscription.documentId,
        }),
      });

      if (response.ok) {
        toast({
          title: 'Subscription riattivata',
          description:
            'La tua subscription continuerà dopo il periodo corrente',
          status: 'success',
          duration: 5000,
        });

        // Ricarica la pagina per mostrare lo stato aggiornato
        window.location.reload();
      } else {
        const errorData = await response.json();
        toast({
          title: 'Errore',
          description:
            errorData.message ||
            'Errore durante la riattivazione della subscription',
          status: 'error',
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('Errore nel riattivare la subscription:', error);
      toast({
        title: 'Errore',
        description: 'Errore di connessione. Riprova più tardi.',
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Funzione per calcolare i giorni rimanenti
  const getDaysRemaining = (currentPeriodEnd) => {
    if (!currentPeriodEnd) return 0;
    const today = new Date();
    const endDate = new Date(currentPeriodEnd);
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  return (
    <Box minH="100vh" bg="#36393f">
      <Container maxW="1200px" py={12}>
        <VStack spacing={16} align="stretch">
          {/* Sezione principale - Layout a due colonne */}
          <Box
            bg="#2f3136"
            p={12}
            borderRadius="20px"
            boxShadow="0 20px 40px rgba(0,0,0,0.3)"
            border="1px solid"
            borderColor="gray.600"
          >
            <SimpleGrid columns={[1, 2]} spacing={12} align="stretch">
              {/* Colonna sinistra - Benvenuto e Vantaggi */}
              <VStack spacing={8} align="start">
                <VStack spacing={6} align="start">
                  <Heading size="xl" color="white" fontWeight="bold">
                    Benvenutə in CIL+
                  </Heading>
                  <Text fontSize="lg" color="gray.300" lineHeight="1.6">
                    Grazie per esserti iscritto!
                    <br />
                    Il tuo gesto è molto più di una semplice subscription: è un
                    segno concreto di supporto alla nostra visione di
                    creatività, inclusione e partecipazione. <br />
                    Con il tuo contributo, stai alimentando un luogo dove la
                    creatività può davvero essere libertà.
                  </Text>
                </VStack>

                {/* Vantaggi Esclusivi */}
                <Box w="full">
                  <Heading size="lg" color="white" mb={4} fontWeight="bold">
                    🎁 I tuoi Vantaggi Esclusivi
                  </Heading>
                  <Text fontSize="md" color="gray.300" mb={6}>
                    Tutti i vantaggi esclusivi inclusi nei tuoi piani CIL+
                    attivi
                  </Text>

                  {loadingTiers ? (
                    <SimpleGrid columns={[1]} spacing={4} w="full">
                      {[1, 2, 3].map((i) => (
                        <Box
                          key={`skeleton-tier-${i}`}
                          bg="white"
                          borderRadius="lg"
                          overflow="hidden"
                          boxShadow="sm"
                          minH="150px"
                        >
                          <Skeleton height="80px" />
                          <Box p={4}>
                            <Skeleton height="24px" mb={3} />
                            <SkeletonText noOfLines={2} mb={3} />
                          </Box>
                        </Box>
                      ))}
                    </SimpleGrid>
                  ) : !Array.isArray(tiers) || tiers.length === 0 ? (
                    <Box
                      bg="#36393f"
                      p={4}
                      borderRadius="12px"
                      boxShadow="sm"
                      border="1px solid"
                      borderColor="#36a9e1"
                      w="full"
                    >
                      <VStack spacing={3}>
                        <Icon as={FaCheckCircle} w={5} h={5} color="#ffcc00" />
                        <Heading size="sm" color="white" textAlign="center">
                          Nessun piano attivo
                        </Heading>
                        <Text fontSize="xs" color="gray.300" textAlign="center">
                          Contatta l&apos;amministrazione per attivare il tuo
                          piano CIL+.
                        </Text>
                      </VStack>
                    </Box>
                  ) : (
                    <VStack spacing={4} align="stretch" w="full">
                      {/* Raccogli tutti i vantaggi esclusivi da tutti i tier attivi */}
                      {(() => {
                        const allVantaggi = [];

                        tiers.forEach((tier) => {
                          if (tier.sconti && tier.sconti.length > 0) {
                            tier.sconti.forEach((sconto) => {
                              // Aggiungi informazioni sul tier per ogni vantaggio
                              allVantaggi.push({
                                ...sconto,
                                tierName: tier.name,
                                tierPrice: tier.price,
                                tierId: tier.documentId,
                              });
                            });
                          }
                        });

                        if (allVantaggi.length === 0) {
                          return (
                            <Box
                              bg="white"
                              p={4}
                              borderRadius="lg"
                              boxShadow="sm"
                              border="1px solid"
                              borderColor="orange.200"
                              w="full"
                            >
                              <VStack spacing={3}>
                                <Icon
                                  as={FaGift}
                                  w={5}
                                  h={5}
                                  color="orange.500"
                                />
                                <Heading
                                  size="sm"
                                  color="orange.600"
                                  textAlign="center"
                                >
                                  Nessun vantaggio esclusivo
                                </Heading>
                                <Text
                                  fontSize="xs"
                                  color="gray.600"
                                  textAlign="center"
                                >
                                  Non ci sono vantaggi esclusivi disponibili per
                                  i tuoi piani attivi.
                                </Text>
                              </VStack>
                            </Box>
                          );
                        }

                        return (
                          <VStack spacing={4} w="full">
                            {allVantaggi.map((vantaggio) => {
                              const TypeIcon = getTypeIcon(vantaggio.type);
                              const typeColor = getTypeColor(vantaggio.type);

                              // Crea una chiave unica basata sui dati del vantaggio
                              const uniqueKey = `vantaggio-${vantaggio.documentId || vantaggio.id || vantaggio.name}-${vantaggio.tierId}`;

                              return (
                                <Box
                                  key={uniqueKey}
                                  bg="white"
                                  borderRadius="lg"
                                  overflow="hidden"
                                  boxShadow="sm"
                                  border="1px solid"
                                  borderColor="gray.200"
                                  w="full"
                                >
                                  <Box p={4}>
                                    <VStack spacing={3} align="stretch">
                                      {/* Header del vantaggio */}
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                      >
                                        <Box display="flex" alignItems="center">
                                          <Icon
                                            as={TypeIcon}
                                            w={5}
                                            h={5}
                                            color={`${typeColor}.500`}
                                            mr={3}
                                          />
                                          <Box>
                                            <Text
                                              fontWeight="bold"
                                              fontSize="md"
                                              color="gray.800"
                                              mb={1}
                                            >
                                              {vantaggio.name}
                                            </Text>
                                            <Text
                                              fontSize="sm"
                                              color="gray.500"
                                            >
                                              Piano: {vantaggio.tierName}
                                            </Text>
                                          </Box>
                                        </Box>

                                        {vantaggio.sponsors &&
                                          vantaggio.sponsors.length > 0 && (
                                            <Badge
                                              bg="#ffcc00"
                                              color="black"
                                              variant="solid"
                                              fontSize="xs"
                                              borderRadius="full"
                                            >
                                              {vantaggio.sponsors.length}{' '}
                                              sponsor
                                            </Badge>
                                          )}
                                      </Box>

                                      {/* Dettagli del vantaggio */}
                                      <Box>
                                        {vantaggio.percentageAmount && (
                                          <Text
                                            fontSize="sm"
                                            color={`${typeColor}.600`}
                                            fontWeight="medium"
                                            mb={2}
                                          >
                                            💰 {vantaggio.percentageAmount}% di
                                            sconto
                                          </Text>
                                        )}
                                        {vantaggio.description && (
                                          <Text
                                            fontSize="sm"
                                            color="gray.700"
                                            lineHeight="1.5"
                                          >
                                            {vantaggio.description}
                                          </Text>
                                        )}
                                      </Box>

                                      {/* Bottone per aprire il modale dei dettagli */}
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                          openVantaggiModal(vantaggio)
                                        }
                                        colorScheme={typeColor}
                                        w="full"
                                        leftIcon={
                                          <Icon as={FaExternalLinkAlt} />
                                        }
                                      >
                                        Visualizza Dettagli
                                      </Button>
                                    </VStack>
                                  </Box>
                                </Box>
                              );
                            })}
                          </VStack>
                        );
                      })()}
                    </VStack>
                  )}
                </Box>
              </VStack>

              {/* Colonna destra - Subscription */}
              <Box>
                {(() => {
                  const activeSubscription = userData.subscriptions?.find(
                    (sub) =>
                      sub.subscriptionStatus === 'active' ||
                      sub.status === 'active'
                  );

                  if (!activeSubscription) {
                    return (
                      <Box
                        bg="#36393f"
                        p={8}
                        borderRadius="20px"
                        border="2px solid"
                        borderColor="#36a9e1"
                        maxW="600px"
                        w="full"
                        mx="auto"
                        textAlign="center"
                      >
                        <VStack spacing={4}>
                          <Icon
                            as={FaCheckCircle}
                            w={8}
                            h={8}
                            color="#ffcc00"
                          />
                          <Heading size="lg" color="white" fontWeight="bold">
                            Nessuna donazione ricorrente attiva
                          </Heading>
                          <Text fontSize="md" color="gray.300">
                            Contatta l&apos;amministrazione per attivare il tuo
                            piano CIL+.
                          </Text>
                        </VStack>
                      </Box>
                    );
                  }

                  return (
                    <VStack spacing={10} w="full">
                      {/* Card subscription attiva - Design elegante e compatto */}
                      <Box
                        bg="#2f3136"
                        p={8}
                        borderRadius="20px"
                        boxShadow="0 20px 40px rgba(0,0,0,0.3)"
                        border="1px solid"
                        borderColor="gray.600"
                        maxW="700px"
                        w="full"
                        mx="auto"
                      >
                        <VStack spacing={8}>
                          {/* Header elegante */}
                          <Box textAlign="center" w="full">
                            <Badge
                              bg={
                                activeSubscription.subscriptionStatus ===
                                'active'
                                  ? '#3eb297'
                                  : '#ffcc00'
                              }
                              color={
                                activeSubscription.subscriptionStatus ===
                                'active'
                                  ? 'white'
                                  : 'black'
                              }
                              variant="solid"
                              fontSize="sm"
                              borderRadius="full"
                              py={2}
                              px={4}
                              fontWeight="bold"
                              mb={4}
                            >
                              {activeSubscription.subscriptionStatus ===
                              'active'
                                ? '✅ Attiva'
                                : `⚠️ ${activeSubscription.subscriptionStatus}`}
                            </Badge>

                            <Heading
                              size="xl"
                              color="white"
                              fontWeight="bold"
                              mb={2}
                            >
                              {activeSubscription.subscriptionTier?.name ||
                                activeSubscription.subscriptionTier?.nome ||
                                'Piano Standard'}
                            </Heading>

                            <Text
                              fontSize="lg"
                              color="#ffcc00"
                              fontWeight="bold"
                            >
                              €{activeSubscription.subscriptionTier?.price || 0}
                              /mese
                            </Text>
                          </Box>

                          {/* Informazioni principali in layout orizzontale */}
                          <Box
                            w="full"
                            p={6}
                            bg="#36393f"
                            borderRadius="12px"
                            border="1px solid"
                            borderColor="gray.600"
                          >
                            <SimpleGrid columns={[1, 2]} spacing={8} w="full">
                              {/* Colonna sinistra - Dati temporali */}
                              <VStack spacing={4} align="stretch">
                                <Box>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="bold"
                                    color="gray.300"
                                    mb={1}
                                  >
                                    Data Inizio
                                  </Text>
                                  <Text
                                    fontSize="md"
                                    color="white"
                                    fontWeight="semibold"
                                  >
                                    {formatDate(activeSubscription.startDate)}
                                  </Text>
                                </Box>

                                <Box>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="bold"
                                    color="gray.300"
                                    mb={1}
                                  >
                                    Periodo Corrente
                                  </Text>
                                  <Text
                                    fontSize="md"
                                    color="white"
                                    fontWeight="semibold"
                                  >
                                    {formatDate(
                                      activeSubscription.currentPeriodStart
                                    )}{' '}
                                    -{' '}
                                    {formatDate(
                                      activeSubscription.currentPeriodEnd
                                    )}
                                  </Text>
                                </Box>

                                {(() => {
                                  const today = new Date();
                                  const start = new Date(
                                    activeSubscription.startDate
                                  );
                                  const daysActive = Math.floor(
                                    (today - start) / (1000 * 60 * 60 * 24)
                                  );

                                  return (
                                    <Box>
                                      <Text
                                        fontSize="sm"
                                        fontWeight="bold"
                                        color="gray.300"
                                        mb={1}
                                      >
                                        Attivo da
                                      </Text>
                                      <Text
                                        fontSize="md"
                                        color="#36a9e1"
                                        fontWeight="semibold"
                                      >
                                        {daysActive} giorni
                                      </Text>
                                    </Box>
                                  );
                                })()}
                              </VStack>

                              {/* Colonna destra - Dati finanziari e stato */}
                              <VStack spacing={4} align="stretch">
                                <Box>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="bold"
                                    color="gray.300"
                                    mb={1}
                                  >
                                    Autorinnovo
                                  </Text>
                                  <Text
                                    fontSize="md"
                                    color={
                                      !activeSubscription.cancelAtPeriodEnd
                                        ? '#3eb297'
                                        : '#e73341'
                                    }
                                    fontWeight="semibold"
                                  >
                                    {!activeSubscription.cancelAtPeriodEnd
                                      ? '✅ Attivo'
                                      : '❌ Disattivo'}
                                  </Text>
                                </Box>

                                {(() => {
                                  // Usa il totale dalle invoice di Stripe se disponibile, altrimenti fallback al calcolo manuale
                                  let totalDonated = 0;
                                  let isLoading = false;

                                  if (totalDonatedFromInvoices !== null) {
                                    totalDonated = totalDonatedFromInvoices;
                                  } else if (loadingInvoices) {
                                    isLoading = true;
                                  } else {
                                    // Fallback al calcolo manuale
                                    const today = new Date();
                                    const start = new Date(
                                      activeSubscription.startDate
                                    );
                                    const monthsActive = Math.floor(
                                      (today.getFullYear() -
                                        start.getFullYear()) *
                                        12 +
                                        (today.getMonth() - start.getMonth())
                                    );
                                    const price =
                                      activeSubscription.subscriptionTier
                                        ?.price || 0;
                                    totalDonated =
                                      (monthsActive > 0 ? monthsActive : 1) *
                                      price;
                                  }

                                  return (
                                    <Box>
                                      <Text
                                        fontSize="sm"
                                        fontWeight="bold"
                                        color="gray.300"
                                        mb={1}
                                      >
                                        Totale Donato
                                      </Text>
                                      {isLoading ? (
                                        <Skeleton height="24px" width="80px" />
                                      ) : (
                                        <Text
                                          fontSize="lg"
                                          color="#ffcc00"
                                          fontWeight="bold"
                                        >
                                          €{totalDonated.toFixed(2)}
                                        </Text>
                                      )}
                                    </Box>
                                  );
                                })()}
                              </VStack>
                            </SimpleGrid>
                          </Box>

                          {/* Azioni subscription */}
                          <VStack spacing={4} w="full">
                            {/* Messaggio di avviso per autorinnovo disattivato */}
                            {activeSubscription.cancelAtPeriodEnd &&
                              activeSubscription.currentPeriodEnd && (
                                <Box
                                  bg="#36393f"
                                  p={4}
                                  borderRadius="12px"
                                  border="2px solid"
                                  borderColor="#ffcc00"
                                  w="full"
                                  textAlign="center"
                                >
                                  <Text
                                    fontSize="md"
                                    color="#ffcc00"
                                    fontWeight="semibold"
                                  >
                                    ⚠️ La tua subscription terminerà il{' '}
                                    {formatDate(
                                      activeSubscription.currentPeriodEnd
                                    )}
                                  </Text>
                                </Box>
                              )}

                            {/* Bottone per riattivare subscription */}
                            {activeSubscription.cancelAtPeriodEnd &&
                              activeSubscription.currentPeriodEnd && (
                                <Button
                                  onClick={reactivateSubscription}
                                  bg="#3eb297"
                                  color="white"
                                  size="md"
                                  w="full"
                                  maxW="500px"
                                  mx="auto"
                                  borderRadius="12px"
                                  fontWeight="600"
                                  _hover={{
                                    bg: '#36a085',
                                    transform: 'translateY(-2px)',
                                    boxShadow:
                                      '0 8px 25px rgba(62, 178, 151, 0.3)',
                                  }}
                                  transition="all 0.3s ease"
                                >
                                  Riattiva Subscription (
                                  {getDaysRemaining(
                                    activeSubscription.currentPeriodEnd
                                  )}{' '}
                                  giorni rimanenti)
                                </Button>
                              )}

                            {/* Bottone per cancellare subscription */}
                            {!activeSubscription.cancelAtPeriodEnd && (
                              <Button
                                onClick={openCancelModal}
                                variant="outline"
                                borderColor="#e73341"
                                color="#e73341"
                                size="md"
                                w="full"
                                maxW="300px"
                                mx="auto"
                                borderRadius="12px"
                                fontWeight="600"
                                _hover={{
                                  bg: '#e73341',
                                  color: 'white',
                                  transform: 'translateY(-2px)',
                                  boxShadow:
                                    '0 8px 25px rgba(231, 51, 65, 0.3)',
                                }}
                                transition="all 0.3s ease"
                              >
                                Cancella Subscription
                              </Button>
                            )}
                          </VStack>

                          {/* Bottone per scaricare fattura */}
                          {activeSubscription.latestInvoiceUrl && (
                            <Box>
                              <Button
                                as="a"
                                href={activeSubscription.latestInvoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="sm"
                                variant="outline"
                                leftIcon={<Icon as={FaExternalLinkAlt} />}
                                bg="#36a9e1"
                                color="white"
                                borderColor="#36a9e1"
                                borderRadius="12px"
                                fontWeight="600"
                                w="full"
                                _hover={{
                                  bg: '#2f95c7',
                                  transform: 'translateY(-2px)',
                                  boxShadow:
                                    '0 8px 25px rgba(54, 169, 225, 0.3)',
                                }}
                                transition="all 0.3s ease"
                              >
                                📄 Visualizza Fattura
                              </Button>
                            </Box>
                          )}
                        </VStack>
                      </Box>
                    </VStack>
                  );
                })()}
              </Box>
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>

      {/* Vantaggi Esclusivi Modal */}
      <VantaggiEsclusiviModal
        isOpen={isVantaggiModalOpen}
        onClose={() => {
          setIsVantaggiModalOpen(false);
          setSelectedSconto(null);
        }}
        sconto={selectedSconto}
      />

      {/* Modal di conferma cancellazione subscription */}
      <Modal
        isOpen={isCancelModalOpen}
        onClose={() => !isCancelling && setIsCancelModalOpen(false)}
        size="md"
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
        <ModalContent
          bg="#2f3136"
          borderRadius="20px"
          boxShadow="0 20px 60px rgba(0, 0, 0, 0.3)"
          border="1px solid"
          borderColor="gray.600"
          color="white"
        >
          <ModalHeader
            textAlign="center"
            py={6}
            borderBottom="1px solid"
            borderColor="gray.600"
          >
            <VStack spacing={3}>
              <Box p={3} bg="#e73341" borderRadius="full" display="inline-flex">
                <Icon as={FaCheckCircle} w={6} h={6} color="white" />
              </Box>
              <Heading size="lg" color="white" fontWeight="bold">
                Cancella Subscription
              </Heading>
            </VStack>
          </ModalHeader>

          <ModalCloseButton
            color="white"
            _hover={{ bg: 'gray.600' }}
            isDisabled={isCancelling}
          />

          <ModalBody p={8}>
            <VStack spacing={6} align="stretch">
              <Text
                fontSize="md"
                color="gray.300"
                textAlign="center"
                lineHeight="1.6"
              >
                Sei sicuro di voler cancellare la tua subscription?
                <br />
                <Text as="span" fontWeight="bold" color="white">
                  La subscription rimarrà attiva fino alla fine del periodo
                  corrente
                </Text>{' '}
                e poi verrà disattivata automaticamente.
              </Text>

              <Box
                p={4}
                bg="#e73341"
                borderRadius="12px"
                border="1px solid"
                borderColor="#d62d3a"
              >
                <Text
                  fontSize="sm"
                  color="white"
                  textAlign="center"
                  fontWeight="medium"
                >
                  ⚠️ Questa azione non può essere annullata
                </Text>
              </Box>

              <VStack spacing={3} w="full">
                <Button
                  onClick={cancelSubscription}
                  bg="#e73341"
                  color="white"
                  size="lg"
                  w="full"
                  isLoading={isCancelling}
                  loadingText="Cancellando..."
                  borderRadius="12px"
                  fontWeight="600"
                  _hover={{
                    bg: '#d62d3a',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(231, 51, 65, 0.3)',
                  }}
                  transition="all 0.3s ease"
                >
                  Sì, cancella la subscription
                </Button>

                <Button
                  onClick={() => setIsCancelModalOpen(false)}
                  variant="outline"
                  borderColor="gray.600"
                  color="gray.300"
                  size="lg"
                  w="full"
                  borderRadius="12px"
                  fontWeight="600"
                  isDisabled={isCancelling}
                  _hover={{
                    bg: 'gray.600',
                    color: 'white',
                    transform: 'translateY(-2px)',
                  }}
                  transition="all 0.3s ease"
                >
                  Annulla
                </Button>
              </VStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CILplus;
